import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useDeleteDocument,
  useDocumentDownloadUrl,
  useListDocuments,
  useUploadDocument,
} from '@/hooks/useDocuments';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

interface TransactionDocumentsProps {
  transactionId: number;
  showDirectReceipt?: boolean;
}

const TransactionDocuments: React.FC<TransactionDocumentsProps> = ({
  transactionId,
  showDirectReceipt = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [viewDoc, setViewDoc] = useState<any | null>(null);
  const {
    data: documents,
    isLoading,
    error,
  } = useListDocuments({ transaction_id: transactionId });
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument();
  const { data: viewUrl, isLoading: viewLoading } = useDocumentDownloadUrl(
    viewDoc?.id,
    { enabled: !!viewDoc },
  );

  useEffect(() => {
    if (showDirectReceipt && documents && documents.length > 0 && !viewDoc) {
      setViewDoc(documents[0]);
    }
  }, [showDirectReceipt, documents, viewDoc]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('transaction_id', String(transactionId));
    uploadMutation.mutate(formData);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Delete this document?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleView = (doc: any) => {
    setViewDoc(doc);
  };

  const handleCloseView = () => {
    setViewDoc(null);
  };

  if (showDirectReceipt && documents && documents.length > 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center relative">
        {viewLoading ? (
          <div className="flex justify-center items-center h-64">
            Loading...
          </div>
        ) : viewUrl ? (
          <div className="w-full h-full flex flex-col items-center justify-center relative">
            {documents[0]?.original_name?.match(/\.(pdf)$/i) ? (
              <div className="relative w-full h-full">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(documents[0].id)}
                  disabled={deleteMutation.isPending}
                  className="absolute top-2 right-2 z-10"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </Button>
                <iframe
                  src={viewUrl}
                  title="PDF Preview"
                  className="w-full h-[70vh] border rounded"
                />
              </div>
            ) : documents[0]?.original_name?.match(
                /\.(jpg|jpeg|png|gif|webp)$/i,
              ) ? (
              <div className="relative">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(documents[0].id)}
                  disabled={deleteMutation.isPending}
                  className="absolute top-2 right-2 z-10"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </Button>
                <img
                  src={viewUrl}
                  alt={documents[0].original_name}
                  className="max-h-[70vh] max-w-full object-contain border rounded"
                />
              </div>
            ) : (
              <div className="relative">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(documents[0].id)}
                  disabled={deleteMutation.isPending}
                  className="absolute top-2 right-2 z-10"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </Button>
                <a
                  href={viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  Download/Open Document
                </a>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-muted-foreground">
            No preview available.
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          id={`txn-doc-upload-${transactionId}`}
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMutation.isPending}
          size="sm"
        >
          Upload Receipt
        </Button>
        {uploadMutation.isPending && <span>Uploading...</span>}
      </div>
      {isLoading && <div>Loading receipts...</div>}
      {error && <div className="text-red-500">Error loading receipts</div>}
      <ul className="space-y-1">
        {documents && documents.length === 0 && <li>No receipts found.</li>}
        {documents?.map((doc: any) => (
          <li key={doc.id} className="flex items-center gap-2 border-b py-1">
            <span className="flex-1 truncate">{doc.original_name}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleView(doc)}
            >
              View
            </Button>
            <Button
              variant="destructive"
              size="xs"
              onClick={() => handleDelete(doc.id)}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </li>
        ))}
      </ul>

      {/* View Document Modal */}
      <Dialog open={!!viewDoc} onOpenChange={handleCloseView}>
        <DialogContent className="max-w-4xl w-full h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              {viewDoc?.original_name || 'View Document'}
            </DialogTitle>
          </DialogHeader>
          {viewLoading ? (
            <div className="flex justify-center items-center h-64">
              Loading...
            </div>
          ) : viewUrl ? (
            <div className="w-full h-full flex flex-col items-center justify-center">
              {viewDoc?.original_name?.match(/\.(pdf)$/i) ? (
                <iframe
                  src={viewUrl}
                  title="PDF Preview"
                  className="w-full h-[70vh] border rounded"
                />
              ) : viewDoc?.original_name?.match(
                  /\.(jpg|jpeg|png|gif|webp)$/i,
                ) ? (
                <img
                  src={viewUrl}
                  alt={viewDoc.original_name}
                  className="max-h-[70vh] max-w-full object-contain border rounded"
                />
              ) : (
                <a
                  href={viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  Download/Open Document
                </a>
              )}
            </div>
          ) : (
            <div className="text-center text-muted-foreground">
              No preview available.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionDocuments;
